import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import useMediaQuery from '../utils/useMediaQuery';
import useDialog from '../utils/useDialog';

const CARD_META = {
  animal:   { icon: '🐾', label: 'Animal',   bg: '#ecfdf5', border: 'rgba(16, 185, 129, 0.25)', text: '#065f46' },
  food:     { icon: '🍽️', label: 'Food',     bg: '#fff7ed', border: 'rgba(249, 115, 22, 0.25)', text: '#9a3412' },
  landmark: { icon: '🏛️', label: 'Place',    bg: '#eff6ff', border: 'rgba(59, 130, 246, 0.25)', text: '#1e40af' },
  fact:     { icon: '✨', label: 'Fun fact',  bg: '#fefce8', border: 'rgba(234, 179, 8, 0.30)',  text: '#854d0e' },
  culture:  { icon: '🎉', label: 'Culture',  bg: '#f5f3ff', border: 'rgba(139, 92, 246, 0.25)', text: '#5b21b6' },
};

/**
 * StickerBook — shows all collected discoveries grouped by country
 *
 * Props:
 *   discoveries — [{ country, type, ts }]
 *   onClose     — () => void  (back to globe)
 */
export default function StickerBook({ discoveries, onClose }) {
  const isMobile = useMediaQuery('(max-width: 640px)');
  const dialogRef = useDialog({ onClose, trapFocus: false });
  const reduced = useReducedMotion();
  // Group by country
  const byCountry = discoveries.reduce((acc, d) => {
    if (!acc[d.country]) acc[d.country] = [];
    acc[d.country].push(d);
    return acc;
  }, {});

  const countries = Object.keys(byCountry).sort();
  const total     = discoveries.length;

  return (
    <motion.div
      className="orboot-scroll"
      style={{ ...s.panel, ...(isMobile ? s.panelMobile : {}) }}
      ref={dialogRef}
      tabIndex={-1}
      role="region"
      aria-label="My sticker book"
      initial={reduced ? { opacity: 0 } : { opacity: 0, y: 24 }}
      animate={reduced ? { opacity: 1 } : { opacity: 1, y: 0 }}
      exit={reduced ? { opacity: 0 } : { opacity: 0, y: 24 }}
      transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
    >

      {/* ── Header ──────────────────────────────────────────────── */}
      <div style={{ ...s.header, ...(isMobile ? s.headerMobile : {}) }}>
        <button style={s.closeBtn} onClick={onClose} aria-label="Back to globe">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
        </button>
        <div style={s.titleBlock}>
          <h1 style={{ ...s.title, ...(isMobile ? s.titleMobile : {}) }}>Sticker Book</h1>
          <p style={s.subtitle}>
            {total === 0
              ? 'No stickers yet — start exploring'
              : `${total} discover${total === 1 ? 'y' : 'ies'} collected`}
          </p>
        </div>
        <span style={{ ...s.bookEmoji, ...(isMobile ? s.bookEmojiMobile : {}) }} aria-hidden="true">📒</span>
      </div>

      {/* ── Empty state ─────────────────────────────────────────── */}
      {total === 0 && (
        <div style={s.emptyWrap}>
          <div style={s.emptyEmoji} aria-hidden="true">🌍</div>
          <p style={s.emptyText}>Tap a country on the globe, then open the cards inside to collect stickers!</p>
          <button style={s.exploreBtn} onClick={onClose}>
            Let's explore!
          </button>
        </div>
      )}

      {/* ── Stickers grouped by country ─────────────────────────── */}
      {countries.map(country => (
        <div key={country} style={{ ...s.countryGroup, ...(isMobile ? s.countryGroupMobile : {}) }}>
          <h2 style={s.countryName}>{country}</h2>
          <div style={s.stickerGrid}>
            {byCountry[country].map(d => {
              const meta = CARD_META[d.type] || CARD_META.fact;
              return (
                <div
                  key={`${d.country}-${d.type}-${d.ts}`}
                  style={{
                    ...s.sticker,
                    background:   meta.bg,
                    border:       `1px solid ${meta.border}`,
                    color:        meta.text,
                  }}
                  role="img"
                  aria-label={`${d.country} ${meta.label} sticker`}
                >
                  <span style={s.stickerIcon} aria-hidden="true">{meta.icon}</span>
                  <span style={s.stickerLabel}>{meta.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* ── Footer encouragement ────────────────────────────────── */}
      {total > 0 && (
        <div style={s.footer}>
          <span style={s.footerStar} aria-hidden="true">⭐</span>
          <p style={s.footerText}>Keep exploring to fill your book!</p>
        </div>
      )}
    </motion.div>
  );
}

const s = {
  panel: {
    position:      'absolute',
    inset:         0,
    background:    '#ffffff',
    color:         '#0f172a',
    overflowY:     'auto',
    paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 100px)',
  },
  panelMobile: {
    paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 88px)',
  },

  header: {
    display:      'flex',
    alignItems:   'center',
    gap:          14,
    padding:      '24px 24px 18px',
    borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
    position:     'sticky',
    top:          0,
    background:   'rgba(255, 255, 255, 0.92)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    zIndex:       2,
  },
  headerMobile: {
    padding: '16px 16px 14px',
    gap: 12,
  },
  closeBtn: {
    width: 40, height: 40,
    borderRadius: '50%',
    border:       '1px solid rgba(15, 23, 42, 0.08)',
    background:   '#ffffff',
    fontSize:     16,
    fontWeight:   600,
    color:        '#0f172a',
    boxShadow:    '0 2px 8px rgba(15, 23, 42, 0.06)',
    flexShrink:   0,
    cursor:       'pointer',
    display:      'flex', alignItems: 'center', justifyContent: 'center',
  },
  titleBlock: { flex: 1 },
  title: {
    fontSize:   24,
    fontWeight: 700,
    color:      '#0f172a',
    margin:     0,
    lineHeight: 1.1,
    letterSpacing: '-0.02em',
  },
  titleMobile: {
    fontSize: 20,
  },
  subtitle: {
    fontSize:   13.5,
    color:      '#64748b',
    fontWeight: 500,
    margin:     '4px 0 0',
    letterSpacing: '-0.005em',
  },
  bookEmoji: { fontSize: 28, opacity: 0.85 },
  bookEmojiMobile: { fontSize: 24 },

  emptyWrap: {
    display:        'flex',
    flexDirection:  'column',
    alignItems:     'center',
    padding:        '64px 32px 40px',
    gap:            18,
    textAlign:      'center',
  },
  emptyEmoji: {
    fontSize:  56,
    animation: 'orboot-bob 2s ease-in-out infinite',
    opacity:   0.85,
  },
  emptyText: {
    fontSize:   15,
    fontWeight: 500,
    color:      '#64748b',
    lineHeight: 1.55,
    maxWidth:   300,
    margin:     0,
  },
  exploreBtn: {
    background:   'linear-gradient(135deg, #fbbf24, #f59e0b)',
    color:        '#0f172a',
    border:       'none',
    borderRadius: 999,
    padding:      '11px 22px',
    fontSize:     14,
    fontWeight:   700,
    boxShadow:    '0 12px 28px rgba(251,191,36,0.32)',
    cursor:       'pointer',
    marginTop:    4,
    letterSpacing: '-0.005em',
  },

  countryGroup: {
    padding:      '18px 24px 0',
  },
  countryGroupMobile: {
    padding: '16px 16px 0',
  },
  countryName: {
    fontSize:     11.5,
    fontWeight:   700,
    color:        '#94a3b8',
    margin:       '0 0 12px',
    display:      'flex',
    alignItems:   'center',
    gap:          6,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  },
  stickerGrid: {
    display:             'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(86px, 1fr))',
    gap:                 10,
    marginBottom:        8,
  },
  sticker: {
    borderRadius:   14,
    padding:        '14px 8px',
    display:        'flex',
    flexDirection:  'column',
    alignItems:     'center',
    gap:            8,
    boxShadow:      '0 1px 2px rgba(15, 23, 42, 0.04)',
    animation:      'orboot-pop-in 300ms cubic-bezier(0.16, 1, 0.3, 1)',
    userSelect:     'none',
    transition:     'transform 200ms var(--ease-out)',
  },
  stickerIcon:  { fontSize: 24, lineHeight: 1 },
  stickerLabel: { fontSize: 14, fontWeight: 700, textAlign: 'center', lineHeight: 1.2, letterSpacing: '0.01em' },

  footer: {
    display:       'flex',
    alignItems:    'center',
    justifyContent:'center',
    gap:           10,
    padding:       '24px 20px 12px',
    borderTop:     '1px solid rgba(15, 23, 42, 0.06)',
    margin:        '24px 24px 0',
  },
  footerStar: {
    fontSize:  20,
  },
  footerText: {
    fontSize:   14,
    fontWeight: 600,
    color:      '#64748b',
    margin:     0,
    letterSpacing: '-0.005em',
  },
};
